import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Grid,
    InputBase,
    InputLabel,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import OnboardingSteps from '../OnboardingSteps/OnboardingSteps';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { GlobalState, useStateMachine } from 'little-state-machine';
import {
    CreateAccountType,
    CreateCompanyAccountRequest,
    CreateProAccountRequest
} from '../../../utils/hooks/api/account/types';
import {
    clearAction,
    updateAction,
    updateStep
} from '../Professional/CreationSteps/updateAction';
import { useCreateCompanyAccount } from '../../../utils/hooks/api/account/useCreateProAccount';
import EmailFormIcon from '../../../assets/icons/EmailFormIcon';
import ErrorFormIcon from '../../../assets/icons/ErrorFormIcon';
import GoogleIcon from '../../../assets/icons/GoogleIcon';
import OnboardingLineIcon from '../../../assets/icons/OnboardingLineIcon';
import PasswordFormIcon from '../../../assets/icons/PasswordFormIcon';
import UserFormIcon from '../../../assets/icons/UserFormIcon';
import axios, { AxiosError } from 'axios';
import SnackAlert from '../../../components/Snackbar';
import { useGoogleAuth } from '../../../utils/hooks/oauth/google/useGoogleAuth';
import { useLinkedInAuth } from '../../../utils/hooks/oauth/linkedin/useLinkedInAuth';
import { logger } from '../../../utils/Helper/logger';
import { UserInfoOAuth } from '../../../utils/hooks/oauth/types/userInfo';
import { LinkedIn } from '@mui/icons-material';
import { isEmailValid } from '../../../utils/Helper/validators';
import useGetVerificationCode from '../../../utils/hooks/api/authentication/useGetVerificationCode';
import { useAuth } from '../../../utils/context/AuthContext';
import CompaniesAutocomplete from '../../../components/CompaniesAutocomplete';

interface ICompanyInfo {
    emailAddress: string;
    password: string;
    companyName: string;
    picture?: string;
    termAgreement: boolean;
    cpassword: string;
}

function CompanyCreate() {
    let navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [companyId, setCompanyId] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const {
        control,
        handleSubmit,
        setError,
        formState,
        register,
        clearErrors,
        setValue,
        getValues
    } = useForm<ICompanyInfo>();
    const { signIn } = useAuth();
    const { isDirty, isValid, errors } = formState;
    const { actions, state, getState } = useStateMachine({
        updateAction,
        updateStep,
        clearAction
    });
    const location = useLocation();
    const { email } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const getCode = useGetVerificationCode();
    const [type, setType] = useState('');
    const [title, setTitle] = useState('Verify email');
    const createAccountMutation = useCreateCompanyAccount();
    const verificationSentMessage =
        'A verification email has been sent to your email address. Click the link in the email to verify your account and continue your signup process.';

    const handleCreateAccount = async (data: GlobalState) => {
        if (document.location.href.includes('company')) {
            setLoading(true);
            const request: CreateCompanyAccountRequest = {
                companyId,
                emailAddress: data.data.emailAddress,
                password: data.data.password,
                picture: data.data.picture,
                companyName: data.data.companyName,
                externalSignIn: !!data.data.externalSignIn
            };

            if (request !== undefined) {
                createAccountMutation.mutate(request, {
                    onSuccess: async (res) => {
                        setLoading(false);
                        actions.clearAction();
                        logger.log('create account =>', res);
                        if (data.data.externalSignIn) {
                            const res: any = await signIn(
                                request.emailAddress,
                                request.password
                            );
                            if (res?.message) navigate('/login');
                            else navigate('/about-company');
                            return;
                        }
                        if (isEmailValid(email) && !queryParams.get('hash')) {
                            createVerification();
                        }
                        navigate(
                            `/create-account/verify-email/${data.data.emailAddress}`
                        );
                    },
                    onError: (error: AxiosError) => {
                        setLoading(false);
                        if (axios.isAxiosError(error)) {
                            console.error(error?.response?.status);
                            if (error?.response?.status === 409) {
                                setMessage('Email already exists!');
                            } else {
                                setMessage('Incorrect credentials!');
                            }
                            setShowAlert(true);
                        }
                    }
                });
            }
        }
    };

    const createVerification = () => {
        if (email) {
            logger.log(
                'connection =>',
                localStorage.getItem('referralConnection')
            );
            setLoading(true);
            getCode.mutate(
                { emailAddress: email, verificationType: 'HASH' },
                {
                    onSuccess: (res) => {
                        logger.log('object', res);
                        setTitle('Verify email');
                        setType('');
                        setMessage(verificationSentMessage);
                        setLoading(false);
                    },
                    onError: () => {
                        setTitle('Account Verification Email Failed');
                        setType('error');
                        setMessage(
                            'There was a problem sending the email, please contact support.'
                        );
                        setLoading(false);
                        return;
                    }
                }
            );
        }
    };

    const onSubmit: SubmitHandler<ICompanyInfo> = async (data) => {
        actions.updateAction(data);
        handleCreateAccount(getState());
    };

    const checkTermAgreement = () => {
        const companyName = control._formValues.companyName;
        if (!companyName?.length) {
            setMessage('Please provide the company name');
            setShowAlert(true);
            return false;
        }
        const agreement = !!control._formValues.termAgreement;
        if (!agreement) {
            setMessage('Please agree to the terms and conditions');
            setShowAlert(true);
        }
        return agreement;
    };
    /** OAuth */
    const handleOAuthLogin = (info: UserInfoOAuth) => {
        const companyInfo = {
            companyName: control._formValues.companyName,
            emailAddress: info.email,
            password: info.sub,
            picture: info.picture,
            termAgreement: true,
            externalSignIn: true
        };
        logger.log('companyInfo =>', companyInfo);
        if (!companyInfo.emailAddress || !checkTermAgreement()) return;
        actions.updateAction(companyInfo);
        handleCreateAccount(getState());
    };
    const handleOAuthError = (error: any) => {
        logger.error(error);
        setMessage('OAuth login failed, please try again');
        setShowAlert(true);
    };
    /** LinkedIn OAuth */
    const { linkedInLogin } = useLinkedInAuth({
        onSuccess: handleOAuthLogin,
        onError: handleOAuthError
    });
    /** Google OAuth */
    const googleLogin = useGoogleAuth({
        onSuccess: handleOAuthLogin,
        onError: handleOAuthError
    });

    const navigateTo = (url: string) => {
        window.location.href = url;
    };

    React.useEffect(() => {
        actions.updateStep(2);
    }, []);

    return (
        <>
            <Box
                sx={{
                    backgroundColor: {
                        md: 'transparent',
                        sm: '#FCFBF8',
                        xs: 'transparent'
                    },
                    borderRadius: '12px',
                    p: { md: 0, sm: '40px', xs: 0 },
                    mt: { md: '50px', sm: '-170px', xs: 0 },
                    mx: { lg: '50px', md: '20px', sm: '50px', xs: '20px' },
                    mb: { md: 0, sm: '50px', sx: 0 }
                }}
            >
                <OnboardingSteps />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        ml: { xl: '30px' },
                        width: { xl: '70%' }
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            mt: { sm: '40px', xs: '20px' },
                            mb: '26px'
                        }}
                    >
                        <Typography
                            fontSize={{ sm: '28px', xs: '18px' }}
                            fontWeight={600}
                            lineHeight={'40px'}
                            sx={{
                                color: '#23282B',
                                display: { sm: 'flex', xs: 'none' },
                                alignSelf: 'start',
                                mb: '32px'
                            }}
                        >
                            Create Account
                        </Typography>
                        <Grid container spacing={'18px'}>
                            <Grid item xs={12}>
                                <Box
                                    sx={{ width: '100%', position: 'relative' }}
                                >
                                    <Controller
                                        {...register('companyName', {
                                            required: 'Required field'
                                        })}
                                        name="companyName"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange,
                                                value,
                                                ref,
                                                onBlur
                                            },
                                            fieldState: { error },
                                            formState
                                        }) => (
                                            <CompaniesAutocomplete
                                                creatable={true}
                                                label="Company name"
                                                placeHolder="Enter company name"
                                                onSelect={(val) => {
                                                    setCompanyId(
                                                        val?.primaryCompanyId ??
                                                            ''
                                                    );
                                                    setValue(
                                                        'companyName',
                                                        val?.primaryCompanyName ??
                                                            ''
                                                    );
                                                    setValue(
                                                        'emailAddress',
                                                        val?.emailAddress ?? ''
                                                    );
                                                    if (val?.emailAddress) {
                                                        clearErrors(
                                                            'emailAddress'
                                                        );
                                                    }
                                                }}
                                                inputStyle={{
                                                    backgroundColor: '#FFFFFF',
                                                    width: '100%',
                                                    height: {
                                                        sm: '65px',
                                                        xs: '45px'
                                                    },
                                                    fontSize: {
                                                        sm: '14px',
                                                        xs: '12px'
                                                    },
                                                    padding: '0px 8px',
                                                    fontFamily: 'open sans',
                                                    color: '#23282B',
                                                    borderBottom:
                                                        '1px solid #D9D9D9',
                                                    mb: '2px',
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            pt: '15px'
                                                        },
                                                    '& 	.MuiInputBase-input': {
                                                        ml: '16px',
                                                        position: 'relative',
                                                        top: '8px'
                                                    },
                                                    '& .MuiInputLabel-formControl':
                                                        {
                                                            top: '16px',
                                                            left: '55px',
                                                            fontSize: {
                                                                sm: '18px',
                                                                xs: '16px'
                                                            },
                                                            fontFamily:
                                                                'Open Sans',
                                                            color: '#23282B'
                                                        }
                                                }}
                                                startAdornment={
                                                    <UserFormIcon
                                                        width={'40'}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                    <Typography
                                        sx={{
                                            color: 'red',
                                            fontSize: '12px',
                                            fontFamily: 'Open Sans'
                                        }}
                                    >
                                        {errors.companyName?.message}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{ width: '100%', position: 'relative' }}
                                >
                                    <InputLabel
                                        sx={{
                                            color: '#23282B',
                                            fontSize: {
                                                sm: '14px',
                                                xs: '12px'
                                            },
                                            position: 'absolute',
                                            top: '8px',
                                            left: '72px',
                                            zIndex: 1,
                                            fontFamily: 'Open Sans',
                                            ml: '3px'
                                        }}
                                        htmlFor="emailAddress"
                                    >
                                        Email
                                    </InputLabel>
                                    {/* email */}
                                    <Controller
                                        {...register('emailAddress', {
                                            required: 'Required field',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                        name="emailAddress"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange,
                                                onBlur,
                                                value,
                                                ref
                                            },
                                            fieldState: { error },
                                            formState
                                        }) => (
                                            <InputBase
                                                required
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(e);
                                                    if (
                                                        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                                                            e.target.value
                                                        )
                                                    ) {
                                                        setError(
                                                            'emailAddress',
                                                            {
                                                                type: 'pattern',
                                                                message:
                                                                    'Invalid email address'
                                                            }
                                                        );
                                                    } else {
                                                        clearErrors(
                                                            'emailAddress'
                                                        );
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (
                                                        !e.target.value &&
                                                        !getValues(
                                                            'emailAddress'
                                                        )
                                                    ) {
                                                        setError(
                                                            'emailAddress',
                                                            {
                                                                type: 'required',
                                                                message:
                                                                    'Please provide a valid email address'
                                                            }
                                                        );
                                                    } else {
                                                        clearErrors(
                                                            'emailAddress'
                                                        );
                                                    }
                                                }}
                                                placeholder="Enter your email address"
                                                error={!!errors?.emailAddress}
                                                inputProps={{
                                                    autoComplete: '',
                                                    form: {
                                                        autoComplete: 'off'
                                                    },
                                                    inputMode: 'email'
                                                }}
                                                inputRef={ref}
                                                id="emailAddress"
                                                startAdornment={
                                                    <EmailFormIcon />
                                                }
                                                rows={1}
                                                sx={{
                                                    backgroundColor: '#FFFFFF',
                                                    width: '100%',
                                                    height: {
                                                        sm: '70px',
                                                        xs: '55px'
                                                    },
                                                    fontSize: {
                                                        sm: '16px',
                                                        xs: '14px'
                                                    },
                                                    padding: '0px 16px',
                                                    fontFamily: 'open sans',
                                                    color: '#23282B',
                                                    mb: '2px',
                                                    borderBottom:
                                                        '1px solid #D9D9D9',
                                                    '& 	.MuiInputBase-input': {
                                                        ml: '20px',
                                                        position: 'relative',
                                                        top: '8px'
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Typography
                                        sx={{
                                            color: 'red',
                                            fontSize: '12px',
                                            fontFamily: 'Open Sans'
                                        }}
                                    >
                                        {errors.emailAddress?.message}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{ width: '100%', position: 'relative' }}
                                >
                                    <InputLabel
                                        sx={{
                                            color: '#23282B',
                                            fontSize: {
                                                sm: '14px',
                                                xs: '12px'
                                            },
                                            position: 'absolute',
                                            top: '8px',
                                            left: '72px',
                                            zIndex: 1,
                                            fontFamily: 'Open Sans',
                                            ml: '3px'
                                        }}
                                        htmlFor="password"
                                    >
                                        Password
                                    </InputLabel>
                                    {/* password */}
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange,
                                                onBlur,
                                                value,
                                                ref
                                            },
                                            fieldState: { error },
                                            formState
                                        }) => (
                                            <InputBase
                                                {...register('password', {
                                                    required: 'Required field',
                                                    minLength: {
                                                        value: 4,
                                                        message:
                                                            'Passwords must be a minimum of 4 characters'
                                                    },
                                                    pattern: {
                                                        value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]).{8,32}$/gm,
                                                        message: 'err'
                                                    }
                                                })}
                                                required
                                                onChange={(e) => {
                                                    onChange(e);
                                                    if (
                                                        e.target.value.length <
                                                        4
                                                    ) {
                                                        setError('password', {
                                                            type: 'minLength',
                                                            message:
                                                                'Passwords must be a minimum of 4 characters'
                                                        });
                                                    } else {
                                                        clearErrors('password');
                                                    }
                                                    if (
                                                        !/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]).{8,32}$/gm.test(
                                                            e.target.value
                                                        )
                                                    ) {
                                                        setError('password', {
                                                            type: 'pattern',
                                                            message:
                                                                'Invalid password'
                                                        });
                                                    } else {
                                                        clearErrors('password');
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (!e.target.value) {
                                                        setError('password', {
                                                            type: 'required',
                                                            message:
                                                                'Please provide a valid password'
                                                        });
                                                    } else {
                                                        clearErrors('password');
                                                    }
                                                }}
                                                error={!!error}
                                                inputProps={{
                                                    autoComplete:
                                                        'new-password',
                                                    form: {
                                                        autoComplete: 'off'
                                                    }
                                                }}
                                                inputRef={ref}
                                                name="password"
                                                id="password"
                                                startAdornment={
                                                    <PasswordFormIcon />
                                                }
                                                rows={1}
                                                placeholder={'********'}
                                                type="password"
                                                sx={{
                                                    backgroundColor: '#FFFFFF',
                                                    width: '100%',
                                                    height: {
                                                        sm: '70px',
                                                        xs: '55px'
                                                    },
                                                    fontSize: {
                                                        sm: '16px',
                                                        xs: '14px'
                                                    },
                                                    padding: '0px 16px',
                                                    mb: '2px',
                                                    fontFamily: 'open sans',
                                                    color: '#23282B',
                                                    borderBottom:
                                                        '1px solid #D9D9D9',
                                                    '& 	.MuiInputBase-input': {
                                                        ml: '20px',
                                                        position: 'relative',
                                                        top: '8px'
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Typography
                                        sx={{
                                            color: 'red',
                                            fontSize: '12px',
                                            fontFamily: 'Open Sans'
                                        }}
                                    >
                                        {errors.password?.message}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: '5px',
                                            mt: '10px',
                                            alignContent: 'start'
                                        }}
                                    >
                                        <ErrorFormIcon />
                                        <Typography
                                            sx={{
                                                color: '#808080',
                                                fontSize: '12px',
                                                fontFamily: 'Open Sans'
                                            }}
                                        >
                                            Password should contain uppercase
                                            letter(s), numbers(s) and special
                                            character(s)
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{ width: '100%', position: 'relative' }}
                                >
                                    <InputLabel
                                        sx={{
                                            color: '#23282B',
                                            fontSize: {
                                                sm: '14px',
                                                xs: '12px'
                                            },
                                            position: 'absolute',
                                            top: '8px',
                                            left: '72px',
                                            zIndex: 1,
                                            fontFamily: 'Open Sans'
                                        }}
                                        htmlFor="cpassword"
                                    >
                                        Confirm Password
                                    </InputLabel>
                                    {/* password */}
                                    <Controller
                                        name="cpassword"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange,
                                                onBlur,
                                                value,
                                                ref
                                            },
                                            fieldState: { error },
                                            formState
                                        }) => (
                                            <InputBase
                                                {...register('cpassword', {
                                                    required: 'Required field',
                                                    minLength: {
                                                        value: 4,
                                                        message:
                                                            'Passwords must be a minimum of 4 characters'
                                                    },
                                                    pattern: {
                                                        value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]).{8,32}$/gm,
                                                        message:
                                                            'Invalid Password!'
                                                    }
                                                })}
                                                required
                                                onChange={(e) => {
                                                    onChange(e);
                                                    if (
                                                        e.target.value.length <
                                                        4
                                                    ) {
                                                        setError('cpassword', {
                                                            type: 'minLength',
                                                            message:
                                                                'Passwords must be a minimum of 4 characters'
                                                        });
                                                    } else if (
                                                        !/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]).{8,32}$/gm.test(
                                                            e.target.value
                                                        )
                                                    ) {
                                                        setError('cpassword', {
                                                            type: 'pattern',
                                                            message:
                                                                'Invalid password'
                                                        });
                                                    } else if (
                                                        e.target.value !==
                                                        control._formValues
                                                            .password
                                                    ) {
                                                        setError('cpassword', {
                                                            type: 'validate',
                                                            message:
                                                                'Password and Confirm Password does not match!'
                                                        });
                                                    } else {
                                                        clearErrors(
                                                            'cpassword'
                                                        );
                                                    }
                                                }}
                                                error={!!error}
                                                inputProps={{
                                                    autoComplete:
                                                        'new-password',
                                                    form: {
                                                        autoComplete: 'off'
                                                    }
                                                }}
                                                inputRef={ref}
                                                name="cpassword"
                                                id="cpassword"
                                                startAdornment={
                                                    <PasswordFormIcon />
                                                }
                                                rows={1}
                                                placeholder={'********'}
                                                type={'password'}
                                                sx={{
                                                    backgroundColor: '#FFFFFF',
                                                    width: '100%',
                                                    height: {
                                                        sm: '70px',
                                                        xs: '55px'
                                                    },
                                                    padding: '0px 16px',
                                                    mb: '2px',
                                                    fontFamily: 'open sans',
                                                    fontSize: {
                                                        sm: '16px',
                                                        xs: '14px'
                                                    },
                                                    color: '#23282B',
                                                    borderBottom:
                                                        '1px solid #D9D9D9',
                                                    '& 	.MuiInputBase-input': {
                                                        ml: '20px',
                                                        position: 'relative',
                                                        top: '8px'
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Typography
                                        sx={{
                                            color: 'red',
                                            fontSize: '12px',
                                            fontFamily: 'Open Sans'
                                        }}
                                    >
                                        {errors.cpassword?.message}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            mb: '20px',
                            width: '100%'
                        }}
                    >
                        <Controller
                            name="termAgreement"
                            control={control}
                            rules={{ required: true }}
                            render={({
                                field: { onChange, onBlur, ref },
                                fieldState: {
                                    invalid,
                                    isTouched,
                                    isDirty,
                                    error
                                },
                                formState
                            }) => {
                                return (
                                    <Checkbox
                                        required
                                        onBlur={onBlur}
                                        onChange={onChange}
                                        inputRef={ref}
                                        name="termAgreement"
                                        inputProps={{
                                            'aria-label':
                                                'I agree to all the Terms and Privacy policy'
                                        }}
                                        sx={{
                                            color: '#D1D1D1',
                                            padding: 0
                                        }}
                                    />
                                );
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontFamily: 'Open Sans',
                                color: '#23282B',
                                ml: '12px'
                            }}
                        >
                            I agree to all the&nbsp;
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontFamily: 'Open Sans',
                                    color: '#055C7F',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                                component={'span'}
                                onClick={() =>
                                    navigateTo(
                                        'https://www.jobfactor.com/policy'
                                    )
                                }
                            >
                                Terms
                            </Typography>{' '}
                            and&nbsp;
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontFamily: 'Open Sans',
                                    color: '#055C7F',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                                component={'span'}
                                onClick={() =>
                                    navigateTo(
                                        'https://www.jobfactor.com/policy'
                                    )
                                }
                            >
                                Privacy policy
                            </Typography>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {loading ? (
                            <CircularProgress sx={{ color: '#05668D' }} />
                        ) : (
                            <Button
                                sx={{
                                    height: { sm: '52px', xs: '45px' }
                                }}
                                variant="contained"
                                disabled={
                                    !isDirty ||
                                    !isValid ||
                                    Object.keys(errors).length !== 0
                                }
                                onClick={handleSubmit(onSubmit)}
                            >
                                <Typography
                                    variant="bodyLargeBold"
                                    textTransform="capitalize"
                                    fontSize={{ sm: '16px', xs: '14px' }}
                                >
                                    Create Account
                                </Typography>
                            </Button>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                justifyContent: 'center',
                                width: '100%',
                                '& svg': {
                                    width: '100%'
                                }
                            }}
                        >
                            <OnboardingLineIcon />
                            <Typography
                                variant="bodyLargeRegular"
                                sx={{
                                    width: '100%',
                                    py: '12px',
                                    backgroundColor: '#fcfbf8',
                                    zIndex: 1,
                                    fontSize: { sm: '16px', xs: '14px' },
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                OR
                            </Typography>
                            <OnboardingLineIcon />
                        </Box>
                        <Grid
                            container
                            sx={{
                                width: '100%',
                                py: '12px',
                                justifyContent: 'space-evenly'
                            }}
                            spacing={1.5}
                        >
                            <Grid item lg={6} md={12} sm={6} xs={12}>
                                <Button
                                    onClick={() => {
                                        if (checkTermAgreement()) googleLogin();
                                    }}
                                    sx={{
                                        height: { sm: '52px', xs: '45px' }
                                    }}
                                    variant="outlined"
                                    startIcon={<GoogleIcon />}
                                >
                                    <Typography
                                        variant="bodyLargeSemiBold"
                                        fontSize={{ sm: '16px', xs: '14px' }}
                                    >
                                        Sign up with Google
                                    </Typography>
                                </Button>
                            </Grid>
                            <Grid item lg={6} md={12} sm={6} xs={12}>
                                <Button
                                    onClick={() => {
                                        if (checkTermAgreement())
                                            linkedInLogin();
                                    }}
                                    sx={{
                                        height: { sm: '52px', xs: '45px' }
                                    }}
                                    variant="outlined"
                                    startIcon={<LinkedIn />}
                                >
                                    <Typography
                                        variant="bodyLargeSemiBold"
                                        fontSize={{ sm: '16px', xs: '14px' }}
                                    >
                                        Sign up with LinkedIn
                                    </Typography>
                                </Button>
                            </Grid>
                        </Grid>

                        <Box
                            sx={{
                                display: 'flex',
                                mt: '24px'
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#808080',
                                    mr: '10px',
                                    fontSize: '14px'
                                }}
                            >
                                Already a Member?
                            </Typography>
                            {/* Text link */}
                            <Typography
                                fontWeight={600}
                                fontSize={{ sm: '16px', xs: '14px' }}
                                fontStyle={'semibold'}
                            >
                                <Link
                                    to={'/login'}
                                    style={{
                                        color: '#05668D',
                                        textDecoration: 'none'
                                    }}
                                >
                                    Sign in
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <SnackAlert
                open={showAlert}
                handleClose={() => setShowAlert(false)}
                message={message}
                type={'error'}
            />
        </>
    );
}

export default CompanyCreate;
